import axios from 'axios';
import { LEGO_CONSTANTS } from 'constants/index';

const MAX_SAFE_BIGINT = BigInt(Number.MAX_SAFE_INTEGER);
const MIN_SAFE_BIGINT = BigInt(Number.MIN_SAFE_INTEGER);

/** Marks values that were JSON *number* literals rewritten as strings; real JSON strings never use this prefix. */
const BIGINT_SENTINEL_PREFIX = '__BIGINT__';

const isDigit = ch => ch >= '0' && ch <= '9';

/** True if `char` is `.`, `e`, or `E` — i.e. JSON number continues as fraction or exponent after digits. */
function isFractionOrExponent(char) {
  return char === '.' || char === 'e' || char === 'E';
}

/** `intPart` is only candidates when length ≥16 (smaller integers fit in safe Number range). */
function shouldQuoteIntegerAsBigInt(intPart) {
  if (!/^-?\d{16,}$/.test(intPart)) {
    return false;
  }
  try {
    const bi = BigInt(intPart);
    return bi > MAX_SAFE_BIGINT || bi < MIN_SAFE_BIGINT;
  } catch (error) {
    if (error instanceof Error) {
      return false;
    }
    throw error;
  }
}

function advanceOverLeadingMinusSign(s, i, end) {
  return i < end && s[i] === '-' ? i + 1 : i;
}

function advanceOverDigits(s, i, end) {
  let j = i;
  while (j < end && isDigit(s[j])) {
    j++;
  }
  return j;
}

function advanceOverFractionIfPresent(s, i, end) {
  if (i >= end || s[i] !== '.') {
    return i;
  }
  return advanceOverDigits(s, i + 1, end);
}

function advanceOverExponentIfPresent(s, i, end) {
  if (i >= end || (s[i] !== 'e' && s[i] !== 'E')) {
    return i;
  }
  let j = i + 1;
  if (j < end && (s[j] === '+' || s[j] === '-')) {
    j++;
  }
  return advanceOverDigits(s, j, end);
}

/**
 * Read one JSON decimal or scientific number literal starting at `startIndex`
 * and returns the end index and the slice to decimal/scientific number.
 * Integer tokens that overflow JS safe integers are not valid here and will be handled by caller.
 */
function extractDecimalOrScientificNumber(entireResponseJsonAsString, startIndex, endIndex) {
  const tokenStart = startIndex;
  let i = advanceOverLeadingMinusSign(entireResponseJsonAsString, startIndex, endIndex);
  i = advanceOverDigits(entireResponseJsonAsString, i, endIndex);
  i = advanceOverFractionIfPresent(entireResponseJsonAsString, i, endIndex);
  i = advanceOverExponentIfPresent(entireResponseJsonAsString, i, endIndex);
  return { endIndex: i, extractedNumber: entireResponseJsonAsString.slice(tokenStart, i) };
}

/**
 * Advance through characters inside a JSON string (incl. escape handling) and copy to `out`.
 * @returns New scan index and string/escape state.
 */
function scanQuotedStringContent(entireResponseJsonAsString, n, i, out, isInsideJsonString, isReadingEscapedChar) {
  if (isReadingEscapedChar) {
    out.push(entireResponseJsonAsString[i++]);
    return { i, isInsideJsonString, isReadingEscapedChar: false };
  }
  const char = entireResponseJsonAsString[i++];
  out.push(char);
  if (char === '\\') {
    return { i, isInsideJsonString, isReadingEscapedChar: true };
  }
  if (char === '"') {
    return { i, isInsideJsonString: false, isReadingEscapedChar: false };
  }
  return { i, isInsideJsonString, isReadingEscapedChar: false };
}

/**
 * Copy a numeric token starting at `i` (and optional unsafe-integer → sentinel rewrite) into `out`.
 * @returns Index after the token
 */
function appendJsonNumericToken(entireResponseJsonAsString, n, i, out) {
  const start = i;
  const char = entireResponseJsonAsString[i];
  if (char === '-') {
    i++;
    if (i >= n || !isDigit(entireResponseJsonAsString[i])) {
      out.push('-');
      return i;
    }
  }
  const integerStartingIndex = start;
  i = advanceOverDigits(entireResponseJsonAsString, i, n);
  const integerAsString = entireResponseJsonAsString.slice(integerStartingIndex, i);
  if (i < n && isFractionOrExponent(entireResponseJsonAsString[i])) {
    const { endIndex, extractedNumber } = extractDecimalOrScientificNumber(entireResponseJsonAsString, start, n);
    out.push(extractedNumber);
    return endIndex;
  }
  if (shouldQuoteIntegerAsBigInt(integerAsString)) {
    out.push(`"${BIGINT_SENTINEL_PREFIX}${integerAsString}"`);
  } else {
    out.push(integerAsString);
  }
  return i;
}

/**
 * Scan raw JSON text; outside quoted strings, replace integer literals that exceed MAX_SAFE_INTEGER
 * with a quoted sentinel string so JSON.parse + reviver can produce BigInt. Floats and scientific
 * notation are copied unchanged.
 */
function rewriteUnsafeJsonIntegerLiterals(entireResponseJsonAsString) {
  const out = [];
  let i = 0;
  const n = entireResponseJsonAsString.length;
  let isInsideJsonString = false;
  let isReadingEscapedChar = false;

  while (i < n) {
    if (isInsideJsonString || isReadingEscapedChar) {
      const st = scanQuotedStringContent(entireResponseJsonAsString, n, i, out, isInsideJsonString, isReadingEscapedChar);
      i = st.i;
      isInsideJsonString = st.isInsideJsonString;
      isReadingEscapedChar = st.isReadingEscapedChar;
      continue;
    }

    const char = entireResponseJsonAsString[i];
    if (char === '"') {
      isInsideJsonString = true;
      out.push(char);
      i++;
      continue;
    }

    if (char === '-' || isDigit(char)) {
      i = appendJsonNumericToken(entireResponseJsonAsString, n, i, out);
      continue;
    }

    out.push(char);
    i++;
  }

  return out.join('');
}

/** Reviver: only JSON numbers rewritten with BIGINT_SENTINEL_PREFIX become BigInt. */
function reviveBigIntStrings(_, value) {
  if (typeof value !== 'string' || !value.startsWith(BIGINT_SENTINEL_PREFIX)) {
    return value;
  }
  try {
    return BigInt(value.slice(BIGINT_SENTINEL_PREFIX.length));
  } catch (error) {
    if (error instanceof Error) {
      return value;
    }
    throw error;
  }
}

/**
 * Parse a JSON string; integer literals outside the safe integer range become `BigInt`
 * (smaller integers stay `number`).
 */
export function parseJsonPreservingUnsafeIntegers(rawJsonString) {
  const processed = rewriteUnsafeJsonIntegerLiterals(rawJsonString);
  return JSON.parse(processed, reviveBigIntStrings);
}

axios.defaults.xsrfCookieName = undefined;
axios.defaults.xsrfHeaderName = undefined;
class AxiosClient {
  constructor() {
    let sessionCSRFToken = null;
    this.client = axios.create({
      timeout: 10000,
      withCredentials: false,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE',
      },
    });

    this.client.interceptors.request.use(
      request => {
        if (sessionCSRFToken) {
          request.headers[LEGO_CONSTANTS.X_XSRF_TOKEN.toUpperCase()] = sessionCSRFToken;
        }
        return request;
      },
      error => Promise.reject(error)
    );

    /**
     * Set up 'transformResponse' to handle JSON parsing with precision preservation
     * for numbers beyond Number.MAX_SAFE_INTEGER range.
     * 'transformResponse' receives the entire raw response as a string
     */
    this.client.defaults.transformResponse = [
      function (entireResponseJsonAsString) {
        if (typeof entireResponseJsonAsString !== 'string') {
          return entireResponseJsonAsString;
        }
        try {
          return parseJsonPreservingUnsafeIntegers(entireResponseJsonAsString);
        } catch (error) {
          if (error instanceof Error) {
            return entireResponseJsonAsString;
          }
          throw error;
        }
      },
    ];

    this.client.interceptors.response.use(
      response => {
        sessionCSRFToken = response.headers[LEGO_CONSTANTS.X_XSRF_TOKEN];
        return response.data;
      },
      error => Promise.reject(error.response)
    );
  }

  /**
   * Serialize request bodies. Mirrors response parsing: `BigInt` becomes JSON *number* tokens
   * (including integers beyond `Number.MAX_SAFE_INTEGER`), not quoted strings.
   * `JSON.stringify` cannot emit BigInt; we feed string values with `BIGINT_SENTINEL_PREFIX` + digits,
   * then unwrap that quoted pattern to a JSON number.
   */
  toJson(data) {
    if (data === undefined) {
      return undefined;
    }
    const json = JSON.stringify(data, (_, v) => (typeof v === 'bigint' ? `${BIGINT_SENTINEL_PREFIX}${v.toString()}` : v));
    const unwrapBigIntInJson = new RegExp(String.raw`"${BIGINT_SENTINEL_PREFIX}(-?\d+)"`, 'g');
    return json.replaceAll(unwrapBigIntInJson, '$1');
  }

  sendRequest(requestConfig) {
    const { url: requestUrl, method: requestMethod, body, headers: formDataHeaders } = requestConfig;
    const url = requestUrl ?? '';
    const method = requestMethod ?? 'GET';
    const formDataRequestHeaders = formDataHeaders?.headers;
    const data = formDataRequestHeaders?.[LEGO_CONSTANTS.CONTENT_TYPE] === LEGO_CONSTANTS.FORMDATA_MULTIPART ? body : this.toJson(body ?? {});
    return this.client.request({
      method,
      url,
      data,
      headers: { ...formDataRequestHeaders },
    });
  }
}

export const axiosClient = new AxiosClient();
